import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { saveIntendedUrl } from '../../utils/persistentNavigation';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { generateFAQSchema, generateBreadcrumbSchema, JsonLd } from '../../utils/structuredData';
import { trackEvent } from '../../utils/analytics';
import { useInMarketingShell } from './MarketingContext';
import useReveal from '../AIEmployees/useReveal';
import { BENCHMARK_MONTH } from '../../content/benchmarkClaim';
import { Volume2, VolumeX } from 'lucide-react';
import '../AIEmployees/AIEmployeesLandingPage.css';
import '../AIEmployees/compare/ComparisonPage.css';
import './ForAgentsPage.css';
import './MusePage.css';

/**
 * MusePage, /muse. "CellCog for Muse": the studio a Meta Muse sends work to.
 * Born Sep 19 2026 (Nitish's directive: a dedicated page like /linear, hero video
 * = the Muse launch film he is producing). Reuses the compare-page visual language
 * (cmp-*) and the for-agents code block (fa-code), like ForAgentsPage.
 *
 * Two audiences on one URL: the human deciding to connect, and the Muse agent that
 * builds the connector from this page (Meta: "Muse can also write its own custom
 * connectors for other services... if they have their own APIs or CLIs"). The
 * markdown alternate at public/muse/index.md says the same things; edit both.
 *
 * Canon: pricing mechanic line + /pricing (no figures), benchmark dated, modes are
 * agents we built, no em dashes, no workforce-replacement framing. Muse is a Meta
 * trademark; the page never implies an official Meta partnership until the
 * directory listing is live (DIRECTORY_STATUS below is the one line to flip).
 *
 * Registrations (agent-readable-web rule): App.js route, seo.py sitemap,
 * react-snap include, LB URL map (Arjun, CEL-2836), .md twin, llms.txt line.
 * Hero video: /assets/muse/hero.mp4 (Arjun drops the file, CEL-2836); the poster
 * shows until it lands.
 */

const FACTS_AS_OF = 'September 19, 2026';
const FACTS_AS_OF_ISO = '2026-09-19';
const DIRECTORY_STATUS = 'coming: CellCog in the Muse connector directory';

/**
 * MuseHeroFilm: the launch film as the FIRST screen of /muse (Nitish, Sep 19 2026 22:54 + 23:22 ET: play on load,
 * full screen under the header, SOUND ON, one display-type overlay top-right, a "Scroll to read" arrow; the written
 * hero moves below it). Sound: browsers allow unmuted autoplay only once the visitor has engaged with the site, so the
 * film first tries with sound; if the browser refuses, it plays muted and turns the sound on at the first tap, click or
 * key anywhere on the page (the scroll arrow included). Loops. 1080p H.264 (browser-safe), poster = the wink frame.
 */
const FILM_SRC = '/assets/muse/hero.mp4?v=dandelion';
const MuseHeroFilm = () => {
  const ref = React.useRef(null);
  const [muted, setMuted] = React.useState(false);

  React.useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    let cancelled = false;
    const unmute = () => {
      if (cancelled || !ref.current) return;
      ref.current.muted = false;
      setMuted(false);
      const p = ref.current.play();
      if (p && p.catch) p.catch(() => {});
      trackEvent('muse_film_sound', { page: 'muse', on: true, source: 'gesture' });
    };
    v.muted = false;
    const attempt = v.play();
    if (attempt && attempt.then) {
      attempt
        .then(() => { if (!cancelled) setMuted(false); })
        .catch(() => {
          if (cancelled) return;
          v.muted = true;
          setMuted(true);
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
          window.addEventListener('pointerdown', unmute, { once: true });
          window.addEventListener('keydown', unmute, { once: true });
        });
    }
    return () => {
      cancelled = true;
      window.removeEventListener('pointerdown', unmute);
      window.removeEventListener('keydown', unmute);
    };
  }, []);

  const toggleSound = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (v.paused) v.play().catch(() => {});
    trackEvent('muse_film_sound', { page: 'muse', on: !v.muted, source: 'button' });
  };
  const scrollToRead = () => {
    const el = document.getElementById('muse-read');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    trackEvent('muse_film_scroll', { page: 'muse' });
  };

  return (
    <section className="muse-film" aria-label="CellCog for Muse launch film">
      <video
        ref={ref}
        className="muse-film__video"
        src={FILM_SRC}
        poster="/assets/muse/hero-poster.jpg?v=dandelion"
        autoPlay
        loop
        playsInline
        preload="auto"
      />
      <div className="muse-film__scrim" aria-hidden="true" />
      <div className="muse-film__copy">
        <span className="muse-film__eyebrow">CellCog for Muse</span>
        <p className="aie-display muse-film__title">Your Muse now has super-agents.</p>
      </div>
      {/* Sound: a quiet round icon while sound is on; it grows a "Sound on" label only when the browser forced the
          muted fallback, so the one tap the visitor needs is spelled out and nothing else competes with the film. */}
      <button
        type="button"
        className={`muse-film__sound ${muted ? 'is-muted' : ''}`}
        onClick={toggleSound}
        aria-label={muted ? 'Turn sound on' : 'Turn sound off'}
        title={muted ? 'Turn sound on' : 'Mute'}
      >
        {muted ? <VolumeX size={16} strokeWidth={1.8} /> : <Volume2 size={16} strokeWidth={1.8} />}
        {muted && <span>Sound on</span>}
      </button>
      {/* Scroll cue: no pill (Nitish, 23:47 ET: the bordered pill "doesn't look cool"); a tracked label and a hairline
          that drips downward, the film-site convention. The whole thing is the button. */}
      <button type="button" className="muse-film__scroll" onClick={scrollToRead} aria-label="Scroll to read">
        <span className="muse-film__scroll-label">Scroll to read</span>
        <span className="muse-film__scroll-line" aria-hidden="true" />
      </button>
    </section>
  );
};

const STEPS = [
  { k: 'Ask Muse', v: '"Turn my launch notes into a 60-second video and a one-page PDF." Muse knows CellCog does this and hands over the brief and the files.' },
  { k: 'Connect once', v: 'The first time, Muse asks you to connect CellCog: sign in or sign up, approve, done. Sentinel asks before CellCog is used, on your terms (once, for this task, or always).' },
  { k: 'Get the work back', v: 'Minutes later the finished files are in your Muse: the video, the PDF, the deck, the spreadsheet, the app link, the report with sources. Muse saves them, sends them, posts them.' },
];

const RETURNS = [
  ['Video', 'ads, reels, explainers and films with dialogue, music and sound'],
  ['Images', 'ad creative, product photos, logos, infographics, edits'],
  ['Documents', 'PDFs, reports, slide decks, one-pagers'],
  ['Spreadsheets', 'models with real formulas, analysis, charts'],
  ['Apps and dashboards', 'working web pages and interactive dashboards, hosted'],
  ['Research', `deep research with sources (ranked #1 on DeepResearch Bench in ${BENCHMARK_MONTH})`],
  ['Audio', 'voiceovers, music, sound effects, podcasts'],
  ['Code and data work', 'prototypes, data analysis, automation scripts'],
];

const CONNECT = [
  {
    k: 'Custom connector (today)',
    v: 'Say "connect CellCog" in Muse. Muse builds the connector itself from the CellCog API, following the skill below. You create an API key at cellcog.ai and hand it to Muse once.',
  },
  {
    k: 'Muse Code (today)',
    v: 'Muse Code speaks MCP. Add https://cellcog.ai/mcp as a streamable HTTP server; sign in once with your CellCog account (OAuth). Thirteen tools, including: create a job, send a message, upload files, check status, get the result, stop a chat, list chats, account. Tested end to end on September 24, 2026 with Muse Code 1.3.0: sign-in, the eight tools of that release, a job created and its result returned.',
  },
  {
    k: 'Muse connector directory',
    v: `${DIRECTORY_STATUS}. When it is listed, connecting is one tap inside Muse, no key to hand over.`,
  },
];

const SKILL_TEXT = `SKILL: CellCog for Muse
Use CellCog when the person asks for finished work that is a file: a video, images,
a PDF or deck, a spreadsheet, a web app or dashboard, or research with sources.
Muse briefs. CellCog makes. Muse delivers.

Connect once: the person creates an API key at https://cellcog.ai/profile?tab=api-keys
and hands it to you as CELLCOG_API_KEY. Never ask for their password.

Run (Python, in your own environment):
  pip install cellcog
  from cellcog import CellCogClient
  client = CellCogClient(agent_provider="muse")
  result = client.create_chat(
      prompt="Turn these notes into a 60-second video and a one-page PDF ...",
      chat_mode="agent",        # agent | creative | team
      timeout=3600,
  )
  # finished files land in ~/.cellcog/chats/<chat_id>/ (or a path you name)

Brief well: name every deliverable and its format. Attach inputs by wrapping each
absolute path in a SHOW_FILE tag inside the prompt (the SDK README shows the exact
form); a bare path is text, not a file. One request may ask for several outputs.

Long jobs: create_chat(..., delivery="send_only") returns at once; poll
client.get_status(chat_id); collect with client.wait_for_completion(chat_id).

Out of credits: the call fails with a message that carries the top-up link. Show it
to the person, then retry after they add credits.

Do not: publish to the person's accounts (that is your job, after the work is back),
pick a model or preset (CellCog chooses), or send files the person did not hand you.`;

const MCP_SNIPPET = `{
  "mcp_servers": {
    "cellcog": {
      "transport": "streamable_http",
      "url": "https://cellcog.ai/mcp",
      "enabled": true
    }
  }
}`;

const FAQ = [
  {
    q: 'Does CellCog act in the world from Muse?',
    a: 'No. CellCog takes a brief and returns finished files. Sending the email, posting the video, paying for something: those stay with Muse, under Sentinel, exactly where Meta put them.',
  },
  {
    q: 'What does it cost?',
    a: 'CellCog is free to try, no credit card needed. After that you pay for the work, not the connection; the cost depends purely on how much work you assign. Current plans are at cellcog.ai/pricing. Muse tokens and CellCog credits are separate.',
  },
  {
    q: 'Who sees my files?',
    a: 'Only your CellCog account. Files you hand over are used for that job; results come back as links valid for 30 days and are downloaded into your Muse. You can revoke the connection any time from your CellCog profile.',
  },
  {
    q: 'Is this an official Meta connector?',
    a: 'Not yet. Today it works as a custom connector Muse builds from our API, and through MCP in Muse Code. We have submitted CellCog to the Muse connector directory; this page says so the day it is listed.',
  },
  {
    q: 'Can Muse hire an AI employee through CellCog?',
    a: 'Not from Muse in this version. CellCog also runs AI employees who own a role over time (a receptionist, an SDR, a researcher); that is the next step, and you can hire one at cellcog.ai/ai-employees today.',
  },
];

const Section = ({ className = '', label, title, children }) => {
  const [ref, visible] = useReveal();
  return (
    <section className={`aie-section cmp-section ${className}`} ref={ref}>
      <div className={`aie-reveal ${visible ? 'is-visible' : ''}`}>
        {(label || title) && (
          <div className="aie-section-head cmp-section-head">
            {label && <span className="cmp-eyebrow">{label}</span>}
            {title && <h2 className="aie-section-title aie-display">{title}</h2>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
};

const MusePage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isDarkMode } = useTheme();
  const inMarketingShell = useInMarketingShell();

  const dark = inMarketingShell ? true : isDarkMode;
  const canonicalUrl = `${SEO_DEFAULTS.url}/muse`;
  const ogImage = `${SEO_DEFAULTS.url}/assets/muse/og-muse.png`;

  const handleStart = (loc) => {
    trackEvent('cta_click', { cta_name: 'muse_get_started', compare_page: 'muse', page: 'muse', location: loc });
    if (!currentUser) {
      saveIntendedUrl('/profile?tab=api-keys');
      navigate('/signup');
      return;
    }
    navigate('/profile?tab=api-keys');
  };

  const seoTitle = 'CellCog for Muse: The Studio Your Muse Sends Work To';
  const seoDescription =
    'Connect Meta Muse to CellCog and get finished work back: video, images, PDFs, spreadsheets, apps and research with sources. Muse briefs, CellCog makes, Muse delivers.';

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      <JsonLd data={generateFAQSchema(FAQ)} />
      <JsonLd
        data={generateBreadcrumbSchema([
          { name: 'Home', url: SEO_DEFAULTS.url },
          { name: 'CellCog for Muse', url: canonicalUrl },
        ])}
      />

      <div className={`aie-landing cmp-page fa-page muse-page ${dark ? 'dark' : 'light'}`}>
        <MuseHeroFilm />
        <section className="cmp-hero" id="muse-read">
          <div className="cmp-hero__glow" aria-hidden="true" />
          <div className="cmp-hero__inner">
            <nav className="cmp-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span>CellCog for Muse</span>
            </nav>
            <span className="cmp-hero__pill">
              <span className="cmp-hero__pill-dot" aria-hidden="true" />
              For Muse
            </span>
            <h1 className="aie-display cmp-hero__title">The studio your Muse sends work to</h1>
            <p className="cmp-hero__sub">
              Muse runs your life. It reads, plans, books and pays. When you ask it for a
              video, a deck, a spreadsheet, an app or a real piece of research, it hands
              the job to CellCog and brings the finished files back into your Muse.
            </p>
            <div className="cmp-hero__ctas">
              <button type="button" className="aie-btn aie-btn-primary" onClick={() => handleStart('hero')}>
                Get started <ArrowRightOutlined />
              </button>
              <a className="aie-btn aie-btn-ghost" href="#muse-connect">
                How to connect
              </a>
            </div>
            <p className="cmp-facts-asof">Facts on this page verified {FACTS_AS_OF}.</p>
          </div>
        </section>

        <Section label="How it works" title="Muse briefs. CellCog makes. Muse delivers.">
          <div className="cmp-table-wrap">
            <table className="cmp-table fa-facts">
              <tbody>
                {STEPS.map((s, i) => (
                  <tr key={s.k}>
                    <th scope="row">{i + 1}. {s.k}</th>
                    <td>{s.v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section label="What comes back" title="Finished work, as files Muse can use">
          <div className="cmp-table-wrap">
            <table className="cmp-table fa-facts">
              <tbody>
                {RETURNS.map(([k, v]) => (
                  <tr key={k}>
                    <th scope="row">{k}</th>
                    <td>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="cmp-facts-asof">
            Every job is done by an agent that writes the script or copy, makes the asset and checks
            its own result before it answers. Benchmark receipts: <Link to="/benchmarks">cellcog.ai/benchmarks</Link>.
          </p>
        </Section>

        <Section className="muse-connect" label="Connect" title="Three ways in, two of them today">
          <div id="muse-connect" className="cmp-table-wrap">
            <table className="cmp-table fa-facts">
              <tbody>
                {CONNECT.map((c) => (
                  <tr key={c.k}>
                    <th scope="row">{c.k}</th>
                    <td>{c.v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="cmp-facts-asof">Muse Code configuration (settings file):</p>
          <pre className="fa-code"><code>{MCP_SNIPPET}</code></pre>
        </Section>

        <Section label="For Muse to read" title="The CellCog skill">
          <div className="cmp-answer">
            <p className="cmp-answer__p">
              This is the connector, written the way Meta writes its own: a skill that tells Muse
              when to hand work to CellCog and how. A Muse reading this page can build the custom
              connector from it; the markdown version is at{' '}
              <a href="/muse/index.md">cellcog.ai/muse/index.md</a>.
            </p>
          </div>
          <pre className="fa-code"><code>{SKILL_TEXT}</code></pre>
        </Section>

        <Section label="Questions" title="Frequently asked">
          <div className="cmp-faq">
            {FAQ.map((f, i) => (
              <details key={i} className="cmp-faq__item">
                <summary className="cmp-faq__q">{f.q}</summary>
                <p className="cmp-faq__a">{f.a}</p>
              </details>
            ))}
          </div>
        </Section>

        <Section label="Keep reading" title="Where the depth lives">
          <div className="cmp-related">
            <Link to="/support/mcp-connector-guide" className="cmp-related__link">
              MCP connector guide <ArrowRightOutlined />
            </Link>
            <Link to="/for-agents" className="cmp-related__link">
              CellCog for AI agents <ArrowRightOutlined />
            </Link>
            <Link to="/pricing" className="cmp-related__link">
              Pricing <ArrowRightOutlined />
            </Link>
            <a href="/blog/what-is-muse/" className="cmp-related__link">
              What Muse is, read plainly <ArrowRightOutlined />
            </a>
          </div>
        </Section>

        <section className="cmp-cta-band">
          <h2 className="aie-display cmp-cta-band__title">Give your Muse a studio.</h2>
          <p className="cmp-cta-band__body">
            Free to try, no credit card needed. Create a CellCog account, make an API key, and tell
            Muse to connect CellCog. The first job can start in the same minute.
          </p>
          <button type="button" className="cmp-cta-band__btn" onClick={() => handleStart('cta_band')}>
            Get started <ArrowRightOutlined />
          </button>
          <p className="muse-trademark">Muse is a trademark of Meta Platforms, Inc. CellCog is an independent product.</p>
        </section>
      </div>
      <span hidden data-facts-as-of={FACTS_AS_OF_ISO} />
    </>
  );
};

export default MusePage;
